import { FC } from 'react'
import { Tab } from '@chegg-ui/tabs'
import {
  Feedback,
  FeedbackType,
  GrammarFeedback,
  ParaphraseFeedback,
} from '@chegg-wt/core-wa/types'
import { SentenceOffset, ToastType } from '@chegg-wt/core-wa/state/types'
import { Dispatch } from 'redux'
import { setFeedbacks } from '@chegg-wt/core-wa/state/actions'
import { FeedbackTab } from '@chegg-wt/core-wa/constants/paraphraser'
import { diffWords } from 'diff'
import { scrollMarkContentInEditor } from '@chegg-wt/core-wa/utils/helpers'
import NicelyDoneBanner from '../NicelyDoneBanner'
import AllCardsAccepted from '../AllCardsAccepted'
import EmptySuggestionsCard from '../EmptySuggestionsCard'
import ClaritySection from './ClaritySection/ClaritySection'
import GrammarSection from './GrammarSection/GrammarSection'
import { CompareDescriptionProps, DiffHighlightProps } from './type'
import { HighlightedText, Sentence } from './ClaritySection/ClarityCard/styled'
import { TOAST_ERRORS } from './constants'

const getActiveTabComponent = (
  grammarFeedbacks,
  paraphraseFeedbacks,
  activeTab,
  isPaperCheckInProgress,
  toastType,
  isAllFeedbacksAcceptedState
) => {
  const grammarLen = grammarFeedbacks.length
  const paraphraseLen = paraphraseFeedbacks.length

  if (activeTab === FeedbackTab.NO_ACTIVE_TAB) {
    return <EmptySuggestionsCard />
  }

  if (isAllFeedbacksAcceptedState) {
    if (TOAST_ERRORS.includes(toastType)) return null

    return <AllCardsAccepted />
  }

  if (activeTab === FeedbackTab.GRAMMAR_TAB) {
    if (grammarLen === 0 && paraphraseLen > 0 && !isPaperCheckInProgress) {
      if (ToastType.GRAMMAR_ERROR === toastType) return null
      return (
        <NicelyDoneBanner
          textMessage="You’ve corrected all the grammar errors we found"
          activeTab={0}
          remainingOtherTabFeedbacks={paraphraseLen}
        />
      )
    }

    return <GrammarSection grammarFeedbacks={grammarFeedbacks} />
  }

  if (paraphraseLen === 0 && grammarLen > 0 && !isPaperCheckInProgress) {
    if (ToastType.CLARITY_ERROR === toastType) return null

    return (
      <NicelyDoneBanner
        textMessage="You’ve ensured your work is clear and convincing"
        activeTab={1}
        remainingOtherTabFeedbacks={grammarLen}
      />
    )
  }

  return <ClaritySection paraphraseFeedbacks={paraphraseFeedbacks} />
}
export const getTabList = (
  activeTabIndex: number,
  grammarFeedbacks: GrammarFeedback[],
  paraphraseFeedbacks: ParaphraseFeedback[],
  isPaperCheckInProgress: boolean,
  toastType: ToastType | null,
  isAllFeedbacksAcceptedState: boolean
): Tab[] => {
  const activeTabComponent = getActiveTabComponent(
    grammarFeedbacks,
    paraphraseFeedbacks,
    activeTabIndex,
    isPaperCheckInProgress,
    toastType,
    isAllFeedbacksAcceptedState
  )
  // adding this code 'panel: activeTabIndex === 0 ? activeTabComponent : null' So that 'activeTabComponent' doesn't render for both Grammar and Clarity.
  return [
    {
      header: 'Grammar',
      panel: activeTabIndex === 0 ? activeTabComponent : null,
    },
    {
      header: 'Clarity',
      panel: activeTabIndex === 1 ? activeTabComponent : null,
    },
  ]
}

export const getFeedbacksByCategory = (
  feedbacks: Feedback[],
  sentenceOffsets: SentenceOffset[]
) => {
  const grammarFeedbacks: GrammarFeedback[] = []
  const paraphraseFeedbacks: ParaphraseFeedback[] = []

  feedbacks.forEach((feedback) => {
    // get sentence offset object
    const sentenceOffset = sentenceOffsets.find(
      (offSet) => offSet.id === feedback.sentenceId
    )
    if (!sentenceOffset) {
      console.log('sentenceOffset not found for feedback!')
      return { grammarFeedbacks, paraphraseFeedbacks }
    }
    if (feedback.feedbackType === FeedbackType.GRAMMAR) {
      const { offset, length } = feedback.position
      // get part of text that was changed
      const impactedText = sentenceOffset.originalText.slice(offset, offset + length)
      grammarFeedbacks.push({
        ...feedback,
        impactedText,
        sentenceOriginalText: sentenceOffset.originalText,
      })
    }

    if (feedback.feedbackType === FeedbackType.PARAPHRASE) {
      paraphraseFeedbacks.push({
        ...feedback,
        impactedText: sentenceOffset.originalText,
      })
    }
  })

  return { grammarFeedbacks, paraphraseFeedbacks }
}

/**
 * Adjusts offSets of Feedbacks in redux-store if multiple feedbacks are present for impacted sentence. Also exclude opposite category feedbacks of same sentence.
 * @param feedbacks latest feedbacks from redux-store
 * @param feedbackApplied current feedback being applied
 * @param dispatch dispatch
 */
export const adjustFeedbacksInRedux = (
  feedbackList: Feedback[],
  feedbackApplied: GrammarFeedback | ParaphraseFeedback,
  dispatch: Dispatch<any>
) => {
  // exclude feedbacks of all other sentences
  const filteredFeedbacksList = feedbackList.filter(
    (feedback) =>
      feedback.sentenceId !== feedbackApplied.sentenceId || // keep other sentences feedbacks
      (feedback.feedbackType === feedbackApplied.feedbackType && // for feedbacks of impacted Sentence, exclude opposite feedback type and currently applied feedback
        feedbackApplied.id !== feedback.id)
  )
  // This case not possible now as for paraphrase we will directly use manual change logic
  // if (feedbackApplied.feedbackType === FeedbackType.PARAPHRASE) {
  //   return dispatch(setFeedbacks(filteredFeedbacksList))
  // }
  const lengthDifference =
    feedbackApplied.suggestedText.length - feedbackApplied.impactedText.length

  const updatedFeedbacks = filteredFeedbacksList.map((currentFeedback) => {
    const isSubsequentGrammarFeedbackForSameSentence =
      currentFeedback.sentenceId === feedbackApplied.sentenceId &&
      currentFeedback.position.offset >
        (feedbackApplied as GrammarFeedback).position.offset &&
      currentFeedback.feedbackType === FeedbackType.GRAMMAR

    if (isSubsequentGrammarFeedbackForSameSentence) {
      return {
        ...currentFeedback,
        position: {
          ...currentFeedback.position,
          offset: currentFeedback.position.offset + lengthDifference,
        },
      }
    }
    return { ...currentFeedback }
  })

  dispatch(setFeedbacks(updatedFeedbacks))
}

export const setNextCardActiveAfterDeleteOrUpdate = (index, feedbacks, setActiveCard) => {
  // set active card when feedbacks are more then one. Since nothing to set active card when only one feedback
  const totalFilteredFeedbacks = feedbacks.length
  let newFeedbackId = null

  if (totalFilteredFeedbacks > 1) {
    const newActiveFeedback = feedbacks[index + 1]
    if (newActiveFeedback) {
      newFeedbackId = newActiveFeedback.id
    } else {
      // if last feedback was deleted or updated
      const lastFeedback = feedbacks[totalFilteredFeedbacks - 2]
      newFeedbackId = lastFeedback.id
    }
  }

  setActiveCard(newFeedbackId)
  scrollMarkContentInEditor(newFeedbackId)
}

const comparedDescription = ({
  originalText,
  suggestedText,
}: CompareDescriptionProps) => {
  const descriptionDiffList = diffWords(originalText, suggestedText)
  const customDesc = descriptionDiffList.map((currentValue, index, arr) => {
    const { added, removed } = currentValue
    if (added && index - 1 >= 0 && arr[index - 1].removed)
      // element is replacement
      return { ...currentValue, replaced: true }
    if (added)
      // element is addition
      return currentValue
    if (removed)
      // element is removed
      return { ...currentValue, value: '' }
    return currentValue // element is unchanged (doesn't have added/removed flag)
  })

  return customDesc
}

// rest operator is needed, so styled component can override css if needed
export const DiffHighlight: FC<DiffHighlightProps> = ({
  originalText,
  suggestedText,
  children,
  ...rest
}) => {
  const diffText = comparedDescription({ originalText, suggestedText })
  return (
    <Sentence {...rest}>
      {diffText.map((part, index) => (
        <HighlightedText
          key={`word-${index}`} /* eslint-disable-line react/no-array-index-key */
          added={part.added}
          removed={part.removed}
        >
          {part.value}
        </HighlightedText>
      ))}
    </Sentence>
  )
}
